import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNil } from 'lodash';
import moment from 'moment/moment';
import { ParsedQuery } from 'query-string';
import { MeasurementDate, MeasurementDateName, MeasurementDatesService } from 'api';
import { EQUITY_VALUE } from 'common/formulas/math.js';
import { CapTableVersion } from 'common/types/company';
import { LESS_DEBT_ALIAS, PLUS_CASH_ALIAS } from 'pages/Waterfall/SpreadsheetConfig/constants';
import useGetFinancialStatementsByMD
  from 'services/hooks/financials/useGetFinancialStatementsByMD/useGetFinancialStatementsByMD';
import { Logger, MOMENT_DEFAULT_DATE_FORMAT, toString } from 'utilities';
import {
  WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION, WATERFALL_QUERY_PARAM_EXIT_DATE,
  WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE, WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE
} from '../constants';

interface UseWaterfallStateInitializationParams {
  companyId?: number;
  urlParams: Record<string, string | undefined>;
  capTableList?: CapTableVersion[] | null;
  companyMeasurementDates: MeasurementDateName[];
  parseUrlParams: (urlParams: Record<string, string | undefined>) => any;
  isFirmPage?: boolean;
  clearUrlParams?: () => void;
  updateCompanyUrl?: (companyId: number) => void;
  updateUrlParams?: (params: Record<string, string | undefined>) => void;
  getCapTableList: (companyId: number, measurementDateId: number) => Promise<void>;
  selectedMeasurementDate?:  MeasurementDate,
    parsedQuery:  ParsedQuery<string>
}

export const useWaterfallStateInitialization = ({
  companyId,
  urlParams,
  capTableList,
  companyMeasurementDates,
  parseUrlParams,
  isFirmPage = false,
  clearUrlParams,
  updateCompanyUrl,
  updateUrlParams,
  getCapTableList,
  selectedMeasurementDate,
  parsedQuery
}: UseWaterfallStateInitializationParams) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(companyId);

  // Update selectedCompanyId when companyId becomes available
  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setSelectedCompanyId(companyId);
    }
  }, [companyId, selectedCompanyId]);

  const [companySelectedMeasurementDate, setCompanySelectedMeasurementDate] = useState<MeasurementDateName>();
  const [selectedCaptable, setSelectedCaptable] = useState<number | undefined>();
  const [equityValue, setEquityValue] = useState<number | null>(null);
  const [enterpriseValue, setEnterpriseValue] = useState<number | null>(null);
  const [cashValue, setCashValue] = useState<number | null>(null);
  const [debtValue, setDebtValue] = useState<number | null>(null);
  const [exitDate, setExitDate] = useState<string | null>(null);
  const [isInitializedFromUrl, setIsInitializedFromUrl] = useState(false);
  const [isInitializingFromUrl, setIsInitializingFromUrl] = useState(false);

  // Refs to track manual state changes
  const hasManuallySetExitDate = useRef(false);
  const hasManuallySetEquityValue = useRef(false);
  const hasManuallySetEnterpriseValue = useRef(false);
  const hasManuallySetCash = useRef(false);
  const hasManuallySetDebt = useRef(false);
  const hasManuallySetCapTable = useRef(false);
  const hasManuallySetCompany = useRef(false);
  const hasManuallySetMeasurementDate = useRef(false);
  
  // Ref to track previous company ID to detect actual company changes
  const previousCompanyId = useRef<number | undefined>(companyId);

  const { data: financials, isLoading: isFinancialsLoading } = useGetFinancialStatementsByMD({ measurementDate: companySelectedMeasurementDate, shouldQueryAutomatically: !!companySelectedMeasurementDate });

  const {plusCash, lessDebt} = useMemo(() => {
    //   we are just going off of the primary financial statement.
    if (financials?.length) {
      const currentFinancials = financials[0];
      const { ltm } = currentFinancials;
      if (ltm) {
        const {balance_sheet} = ltm;
        return {
          plusCash: Number(balance_sheet?.total_cash_equivalents || 0),
          lessDebt: Number(balance_sheet?.total_debt  || 0),
        }
      }
    }
    return {plusCash: 0, lessDebt: 0};
  }, [financials]);

  // Initialize state from URL parameters or default values
  useEffect(() => {
    if (!isInitializedFromUrl) {
      if (Object.keys(urlParams).length > 0) {
        // Initialize from URL parameters
        const parsedState = parseUrlParams(urlParams);
        
        if (parsedState.selectedCompanyId) {
          setSelectedCompanyId(parsedState.selectedCompanyId);
          hasManuallySetCompany.current = true;
        }
        if (parsedState.companySelectedMeasurementDate) {
          setCompanySelectedMeasurementDate(parsedState.companySelectedMeasurementDate);
          hasManuallySetMeasurementDate.current = true;
        }
        
        if (parsedState.selectedCaptable) {
          setSelectedCaptable(parsedState.selectedCaptable);
          hasManuallySetCapTable.current = true;
        }
        
        if (parsedState.exitDate) {
          setExitDate(parsedState.exitDate);
          hasManuallySetExitDate.current = true;
        }

        if (parsedState.enterpriseValue) {
          setEnterpriseValue(parsedState.enterpriseValue);
          hasManuallySetEnterpriseValue.current = true;
        }

        if (parsedState.equityValue) {
          setEquityValue(parsedState.equityValue);
          hasManuallySetEquityValue.current = true;
        }

        // cash/debt from URL (zero must be respected): check against undefined/null, not falsy
        if (isNil(parsedState.plusCash) === false) {
          setCashValue(parsedState.plusCash);
          hasManuallySetCash.current = true;
        }
        if (isNil(parsedState.lessDebt) === false) {
          setDebtValue(parsedState.lessDebt);
          hasManuallySetDebt.current = true;
        }
      } else if (isFirmPage) {
        // For firm pages without deeplink, start with clean state
        setSelectedCompanyId(undefined);
        setCompanySelectedMeasurementDate(undefined);
        setSelectedCaptable(undefined);
        setEquityValue(null);
        setEnterpriseValue(null);
        setCashValue(null);
        setDebtValue(null);
        setExitDate(null);
      } else if (companyId) {
        // For non-firm pages, use the companyId from store
        setSelectedCompanyId(companyId);
      }
      setIsInitializedFromUrl(true);
    }
  }, [companyId, urlParams, isInitializedFromUrl, parseUrlParams, isFirmPage, clearUrlParams]);

  // Re-parse URL parameters when companyMeasurementDates becomes available
  useEffect(() => {
    if (Object.keys(urlParams).length > 0 && isInitializedFromUrl && companyMeasurementDates?.length > 0) {
      const parsedState = parseUrlParams(urlParams);
      
      // Only update measurement date if it wasn't manually set and we found a valid one in URL
      if (parsedState.companySelectedMeasurementDate && !hasManuallySetMeasurementDate.current) {
        setCompanySelectedMeasurementDate(parsedState.companySelectedMeasurementDate);
        hasManuallySetMeasurementDate.current = true;
      }
    }
  }, [companyMeasurementDates, urlParams, isInitializedFromUrl, parseUrlParams, setCompanySelectedMeasurementDate]);

  useEffect(() => {
    if (selectedMeasurementDate) {
      setCompanySelectedMeasurementDate(selectedMeasurementDate)
    }
  }, [selectedMeasurementDate, setCompanySelectedMeasurementDate]);

  // initialize the captable list based purely on the company selected measurement date
  useEffect(() => {
    if (selectedCompanyId && companySelectedMeasurementDate?.id && companySelectedMeasurementDate.company_id === selectedCompanyId && companySelectedMeasurementDate.slug === parsedQuery.date) {
      getCapTableList(selectedCompanyId, companySelectedMeasurementDate.id);
    }
  }, [companySelectedMeasurementDate, getCapTableList, selectedCompanyId, parsedQuery]);


  // Initialize measurement dates for selected company - this is used exclusively by the firm waterfall
  useEffect(() => {
    if (selectedCompanyId && !selectedMeasurementDate) {
      MeasurementDatesService.apiMeasurementDatesCompanyRead(toString(selectedCompanyId))
        .then(data => {
          // Find the most recent measurement date
          const mostRecentMeasurementDate = data.toSorted(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )[0];

          // Always set the most recent measurement date when company changes
          if (mostRecentMeasurementDate?.id) {
            setCompanySelectedMeasurementDate(mostRecentMeasurementDate);
            getCapTableList(selectedCompanyId, mostRecentMeasurementDate.id);
          }
        })
        .catch(error => {
          Logger.error('Error fetching measurement dates:', error);
        });
    }
  }, [getCapTableList, selectedCompanyId, selectedMeasurementDate, setCompanySelectedMeasurementDate]);

  // Reset values when company changes
  useEffect(() => {
    // Only run if this is an actual company change (not initial load from URL)
    if (selectedCompanyId && selectedCompanyId !== previousCompanyId.current && isInitializedFromUrl && previousCompanyId.current !== undefined) {
      previousCompanyId.current = selectedCompanyId;
      
      // Always reset cap table when company changes since cap table IDs are company-specific
      setSelectedCaptable(undefined);
      
      if (isFirmPage && updateCompanyUrl) {
        // For firm pages, update the company URL parameter
        updateCompanyUrl(selectedCompanyId);
      } else if (updateUrlParams) {
        // For non-firm pages, clear only company-specific parameters and reset manual values
        updateUrlParams({
          [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: undefined,
          [WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]: undefined,
          [WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]: undefined,
          [WATERFALL_QUERY_PARAM_EXIT_DATE]: undefined,
          // clear cash/debt overrides on company change so we fall back to financials
          [PLUS_CASH_ALIAS]: undefined,
          [LESS_DEBT_ALIAS]: undefined,
        });
        // Reset manual values when company changes
        setEquityValue(null);
        setEnterpriseValue(null);
        setCashValue(null);
        setDebtValue(null);
        setExitDate(null);
      }
      
      // Reset manual flags for next company change
      hasManuallySetEquityValue.current = false;
      hasManuallySetEnterpriseValue.current = false;
      hasManuallySetCapTable.current = false;
      hasManuallySetExitDate.current = false;
      hasManuallySetCash.current = false;
      hasManuallySetDebt.current = false;
    } else if (selectedCompanyId && previousCompanyId.current === undefined) {
      // This is the initial load, just set the previous company ID without clearing parameters
      previousCompanyId.current = selectedCompanyId;
    }
  }, [clearUrlParams, selectedCompanyId, isFirmPage, updateCompanyUrl, updateUrlParams, isInitializedFromUrl]);

  // Initialize cap table selection
  useEffect(() => {
    if (capTableList && capTableList.length > 0 && !capTableList.map(captable => captable.id).includes(selectedCaptable)) {
      const primaryCapTable = capTableList.find(captable => captable.is_primary);
      if (primaryCapTable?.id) {
        setSelectedCaptable(primaryCapTable.id);
      }
    }
  }, [capTableList, selectedCaptable]);

  // Initialize exit date
  useEffect(() => {
    if (!exitDate && !hasManuallySetExitDate.current) {
      if (companySelectedMeasurementDate?.date) {
        setExitDate(moment(companySelectedMeasurementDate.date).format(MOMENT_DEFAULT_DATE_FORMAT));
      } else {
        setExitDate(moment().format(MOMENT_DEFAULT_DATE_FORMAT));
      }
    }
  }, [companySelectedMeasurementDate, exitDate]);

  // Clear initializing flag when all required data is loaded
  useEffect(() => {
    if (isInitializedFromUrl && selectedCompanyId && companySelectedMeasurementDate && selectedCaptable) {
      setIsInitializingFromUrl(false);
    }
  }, [isInitializedFromUrl, selectedCompanyId, companySelectedMeasurementDate, selectedCaptable]);

  // Handler functions for manual state changes
  const handleCompanyChange = useCallback((value: number) => {
    setSelectedCompanyId(value);
    hasManuallySetCompany.current = true;
  }, []);

  const handleMeasurementDateChange = useCallback((value: number) => {
    const measurementDate = companyMeasurementDates?.find(md => md.id && toString(md.id) === toString(value));
    if (measurementDate) {
      setCompanySelectedMeasurementDate(measurementDate);
      hasManuallySetMeasurementDate.current = true;
    }
  }, [companyMeasurementDates]);

  const handleCapTableChange = useCallback((value: number) => {
    setSelectedCaptable(value);
    hasManuallySetCapTable.current = true;
  }, []);

  const handleExitDateChange = useCallback((value: string) => {
    setExitDate(value);
    hasManuallySetExitDate.current = true;
  }, []);

  const handleEnterpriseValueChange = useCallback((value: number) => {
    setEnterpriseValue(value);
    hasManuallySetEnterpriseValue.current = true;
    setEquityValue(null);
    hasManuallySetEquityValue.current = false;
  }, []);

  const handleEquityValueChange = useCallback((value: number) => {
    setEquityValue(value);
    hasManuallySetEquityValue.current = true;
    setEnterpriseValue(null);
    hasManuallySetEnterpriseValue.current = false;
  }, []);

  const updateNumericValue = useCallback(
    (
      value: number | null | undefined,
      setter: (v: number | null) => void,
      manualFlagRef: React.RefObject<boolean>,
    ) => {
      if (isNil(value)) {
        // null/undefined means fall back to financials
        setter(null);
      } else {
        setter(Number(value));
      }
      // eslint-disable-next-line no-param-reassign
      manualFlagRef.current = true;
    },
    [],
  );

  const handleCashChange = useCallback(
    (value: number | null | undefined) => {
      updateNumericValue(value, setCashValue, hasManuallySetCash);
    },
    [updateNumericValue],
  );

  const handleDebtChange = useCallback(
    (value: number | null | undefined) => {
      updateNumericValue(value, setDebtValue, hasManuallySetDebt);
    },
    [updateNumericValue],
  );

  // Determine effective cash/debt prioritizing manual/url overrides; zero is valid
  const effectivePlusCash = useMemo(() => (cashValue ?? plusCash), [cashValue, plusCash]);
  const effectiveLessDebt = useMemo(() => (debtValue ?? lessDebt), [debtValue, lessDebt]);

  // Flags to indicate source of cash/debt (true means using financials fallback)
  const plusCashIsFromFinancials = useMemo(() => cashValue === null || cashValue === undefined || plusCash === cashValue, [cashValue, plusCash]);
  const lessDebtIsFromFinancials = useMemo(() => debtValue === null || debtValue === undefined || lessDebt === debtValue, [debtValue, lessDebt]);

  const calculatedEquityValue = useMemo(() => {
    if (enterpriseValue !== null) {
      return EQUITY_VALUE(Number(enterpriseValue), Number(effectivePlusCash ?? 0), Number(effectiveLessDebt ?? 0));
    }
    return equityValue;

  }, [enterpriseValue, equityValue, effectivePlusCash, effectiveLessDebt]);

  return {
    // State
    selectedCompanyId,
    companySelectedMeasurementDate,
    selectedCaptable,
    enterpriseValue,
    equityValue,
    exitDate,
    isInitializedFromUrl,
    isInitializingFromUrl,

    // Setters
    setSelectedCompanyId,
    setCompanySelectedMeasurementDate,
    setSelectedCaptable,
    setEquityValue,
    setEnterpriseValue,
    setExitDate,
    setIsInitializedFromUrl,
    setIsInitializingFromUrl,

    // Manual change handlers
    handleCompanyChange,
    handleMeasurementDateChange,
    handleCapTableChange,
    handleExitDateChange,
    handleEquityValueChange,
    handleEnterpriseValueChange,
    handleCashChange,
    handleDebtChange,

    // Manual change flags
    hasManuallySetExitDate,
    hasManuallySetEquityValue,
    hasManuallySetEnterpriseValue,
    hasManuallySetCapTable,
    hasManuallySetCompany,
    hasManuallySetMeasurementDate,

    // financials and effective values
    plusCash: effectivePlusCash,
    lessDebt: effectiveLessDebt,
    plusCashIsFromFinancials,
    lessDebtIsFromFinancials,
    calculatedEquityValue,
    isFinancialsLoading
  };
};
